import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import QRCode from 'qrcode'
import type { User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { ReplyTone, UserPreferences, UserProfile } from '@/lib/types'
import { ToneSlider } from './ToneSlider'
import { DEFAULT_THEME, THEMES } from '@/lib/themes'
import { SUPPORTED_LOCALES } from '@/i18n'
import { AUDIO_SPEEDS, DEFAULT_AUDIO_SPEED } from './InlineAudio'
import { cn } from '@/lib/utils'
import { useConfirm } from './ConfirmDialog'
import {
  X,
  Loader2,
  Camera,
  Check,
  ShieldCheck,
  ShieldOff,
  Palette,
  Captions,
  Sparkles,
  HelpCircle,
  CalendarDays,
  Flame,
  TrendingUp,
  UserCircle,
  Languages,
  Settings as SettingsIcon,
  Gauge,
  Building2,
  MessageCircle,
  CreditCard,
  Bell,
  Inbox,
  Mail,
  LogOut as LogOutIcon,
  ArrowRightLeft as ArrowRightLeftIcon,
  Archive as ArchiveIcon,
  Trash2,
  Flag,
  UserPlus as UserPlusIcon,
  MessageSquare,
  ArrowDownWideNarrow,
  Volume2,
  Truck,
  Copy,
  Download,
  RefreshCw,
  ExternalLink,
} from 'lucide-react'

interface Props {
  open: boolean
  user: User
  profile: UserProfile | null
  preferences: UserPreferences
  onUpdatePreferences: (patch: Partial<UserPreferences>) => void | Promise<void>
  /** Patch any subset of profile fields. Each AutoSaveField fires this
   *  on blur (when its value changed), so the modal never needs a save
   *  button. The handler in App.tsx writes to profiles.* via Supabase +
   *  does the optimistic update. */
  onUpdateProfile: (patch: Partial<UserProfile>) => Promise<void>
  /** Called after a successful profile save with the new name + avatar
   *  so App can patch the local profilesById immediately, instead of
   *  waiting on the realtime echo (which can lag or drop on tabs that
   *  fell asleep). */
  onProfilePatched?: (patch: {
    display_name: string | null
    avatar_url: string | null
  }) => void
  /** True when the user owns at least one channel (account holder). Guests
   *  (invited collaborators with no own channel) don't see the WhatsApp
   *  Business, Billing and Business-announcement tabs — those are about the
   *  account holder's own WhatsApp number. */
  isAccountOwner?: boolean
  onClose: () => void
}

type TabKey =
  | 'profile'
  | 'company'
  | 'whatsapp'
  | 'billing'
  | 'preferences'
  | 'ai'
  | 'notifications'
  | 'security'
  | 'qrcode'

/** ISO 3166-1 alpha-2 codes of the countries we surface in dropdowns.
 *  Limited to the markets msgban realistically targets (EU + Americas +
 *  MENA + Africa + APAC). The display label is resolved at runtime via
 *  `Intl.DisplayNames` in the user's locale — adding a country = just
 *  appending its code. */
const COUNTRY_CODES = [
  'FR', 'CH', 'BE', 'LU', 'DE', 'AT', 'IT', 'ES', 'PT', 'NL', 'IE', 'GB',
  'DK', 'SE', 'NO', 'FI', 'PL', 'CZ', 'GR', 'RO',
  'CA', 'US', 'MX', 'BR', 'AR', 'CL', 'CO', 'PE',
  'AE', 'SA', 'QA', 'KW', 'BH', 'OM', 'JO', 'EG', 'MA', 'TN', 'DZ', 'IL',
  'SN', 'CI', 'CM', 'NG', 'KE', 'ZA',
  'IN', 'SG', 'HK', 'MY', 'PH', 'ID', 'VN', 'TH', 'JP', 'KR', 'AU', 'NZ',
] as const

function initials(nameOrEmail: string): string {
  const base = nameOrEmail.split('@')[0].replace(/[^\p{L}\p{N}\s.]/gu, ' ').trim()
  const parts = base.split(/[\s.]+/).filter(Boolean).slice(0, 2)
  return parts.map((p) => p[0]?.toUpperCase()).join('') || '?'
}

/**
 * Account / settings modal, organised as a vertical-tabbed settings page
 * with **auto-save per field** (Notion / Linear / Stripe style — no
 * « Save » button anywhere). Each text field saves on blur; selects /
 * checkboxes save on change. Visual confirmation = brief check icon
 * next to the field label.
 *
 * Seven tabs:
 *   - Profile           : avatar + first/last/display name + phone + email
 *   - Company           : legal + commercial identity + registered address
 *   - WhatsApp Business : display name + about + category (Cloud API future)
 *   - Billing           : invoice address + VAT + invoices email (Stripe future)
 *   - Preferences       : language, theme, audio playback speed
 *   - AI                : auto-transcribe + 4 signal-detection toggles
 *   - Security          : two-factor authentication (TOTP)
 *
 * Modal has a **fixed height** (h-[85vh], min-h-[600px]), the content
 * panel scrolls independently. Switching tabs never resizes the modal.
 */
export function ProfileModal({
  open,
  user,
  profile,
  preferences,
  onUpdatePreferences,
  onUpdateProfile,
  onProfilePatched,
  isAccountOwner = true,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation()
  const meta = user.user_metadata ?? {}
  const [activeTab, setActiveTab] = useState<TabKey>('profile')

  // ── Two-factor authentication ───────────────────────────────────────────
  const confirm = useConfirm()
  const [factors, setFactors] = useState<{ id: string; status: string }[]>([])
  const [mfaLoading, setMfaLoading] = useState(true)
  const [enrolling, setEnrolling] = useState<{
    id: string
    qr: string
    secret: string
  } | null>(null)
  const [mfaCode, setMfaCode] = useState('')
  const [mfaBusy, setMfaBusy] = useState(false)
  const [mfaError, setMfaError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setMfaLoading(true)
    supabase.auth.mfa.listFactors().then(({ data }) => {
      if (cancelled) return
      setFactors(
        (data?.totp ?? []).map((f) => ({
          id: f.id,
          status: String(f.status),
        })),
      )
      setMfaLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [open])

  if (!open) return null

  const verifiedFactor = factors.find((f) => f.status === 'verified')

  async function refreshFactors() {
    const { data } = await supabase.auth.mfa.listFactors()
    setFactors(
      (data?.totp ?? []).map((f) => ({ id: f.id, status: String(f.status) })),
    )
  }

  async function startEnroll() {
    setMfaError(null)
    setMfaBusy(true)
    try {
      const { data: existing } = await supabase.auth.mfa.listFactors()
      for (const f of existing?.all ?? []) {
        if (String(f.status) === 'unverified') {
          await supabase.auth.mfa.unenroll({ factorId: f.id }).catch(() => {})
        }
      }
      const { data, error: err } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        issuer: 'msgban',
        friendlyName: `msgban — ${user.email ?? 'account'}`,
      })
      if (err) throw err
      setEnrolling({
        id: data.id,
        qr: data.totp.qr_code,
        secret: data.totp.secret,
      })
      setMfaCode('')
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : String(err))
    } finally {
      setMfaBusy(false)
    }
  }

  async function confirmEnroll() {
    if (!enrolling || mfaCode.trim().length < 6) return
    setMfaError(null)
    setMfaBusy(true)
    try {
      const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({
        factorId: enrolling.id,
      })
      if (chErr) throw chErr
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId: enrolling.id,
        challengeId: ch.id,
        code: mfaCode.trim(),
      })
      if (vErr) throw vErr
      await refreshFactors()
      setEnrolling(null)
      setMfaCode('')
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : String(err))
    } finally {
      setMfaBusy(false)
    }
  }

  async function cancelEnroll() {
    if (enrolling) {
      await supabase.auth.mfa.unenroll({ factorId: enrolling.id }).catch(() => {})
    }
    setEnrolling(null)
    setMfaCode('')
    setMfaError(null)
  }

  async function disableMfa(factorId: string) {
    const ok = await confirm({
      title: t('profile.sections.security.twoFactorDisable'),
      message: t('profile.sections.security.description'),
      confirmLabel: t('profile.sections.security.twoFactorDisable'),
    })
    if (!ok) return
    setMfaBusy(true)
    setMfaError(null)
    try {
      const { error: err } = await supabase.auth.mfa.unenroll({ factorId })
      if (err) throw err
      await refreshFactors()
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : String(err))
    } finally {
      setMfaBusy(false)
    }
  }

  // Tab metadata — single source of truth for the sidebar rendering AND
  // the content-panel switch.
  const TABS: { key: TabKey; icon: React.ReactNode; label: string }[] = [
    { key: 'profile', icon: <UserCircle className="h-4 w-4" />, label: t('profile.tabs.profile') },
    { key: 'company', icon: <Building2 className="h-4 w-4" />, label: t('profile.tabs.company') },
    // Owner-only tabs (own WhatsApp number / billing / migration announcement).
    ...(isAccountOwner
      ? ([
          { key: 'whatsapp', icon: <MessageCircle className="h-4 w-4" />, label: t('profile.tabs.whatsapp') },
          { key: 'billing', icon: <CreditCard className="h-4 w-4" />, label: t('profile.tabs.billing') },
        ] as { key: TabKey; icon: React.ReactNode; label: string }[])
      : []),
    { key: 'preferences', icon: <SettingsIcon className="h-4 w-4" />, label: t('profile.tabs.preferences') },
    // AI settings are owner-only — invited collaborators can't access them.
    ...(isAccountOwner
      ? ([{ key: 'ai', icon: <Sparkles className="h-4 w-4" />, label: t('profile.tabs.ai') }] as {
          key: TabKey
          icon: React.ReactNode
          label: string
        }[])
      : []),
    { key: 'notifications', icon: <Bell className="h-4 w-4" />, label: t('profile.tabs.notifications') },
    { key: 'security', icon: <ShieldCheck className="h-4 w-4" />, label: t('profile.tabs.security') },
    ...(isAccountOwner
      ? ([{ key: 'qrcode', icon: <Truck className="h-4 w-4" />, label: t('profile.tabs.qrcode') }] as {
          key: TabKey
          icon: React.ReactNode
          label: string
        }[])
      : []),
  ]

  return (
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center bg-slate-900/30 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex h-[85vh] min-h-[600px] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--color-border-soft)] px-6 py-4">
          <div className="min-w-0">
            <h3 className="text-[15px] font-semibold text-[var(--color-text-strong)]">
              {t('profile.title')}
            </h3>
            <p className="mt-0.5 text-[11.5px] text-[var(--color-text-dim)]">
              {t('profile.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-1.5 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
            aria-label={t('profile.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body — vertical-tab layout */}
        <div className="flex min-h-0 flex-1">
          <nav className="flex shrink-0 flex-col gap-1 overflow-y-auto border-r border-[var(--color-border-soft)] bg-[var(--color-surface-2)] p-3 sm:w-52">
            {TABS.map((tab) => {
              const active = tab.key === activeTab
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[12.5px] font-medium transition-colors',
                    active
                      ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                      : 'text-[var(--color-text-dim)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]',
                  )}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              )
            })}
          </nav>

          <div className="min-w-0 flex-1 overflow-y-auto px-6 py-5">
            {activeTab === 'profile' && (
              <ProfileTabPanel
                t={t}
                user={user}
                profile={profile}
                fallbackAvatarUrl={meta.avatar_url ?? null}
                onUpdateProfile={onUpdateProfile}
                onProfilePatched={onProfilePatched}
              />
            )}
            {activeTab === 'company' && (
              <CompanyTabPanel
                t={t}
                locale={i18n.language}
                profile={profile}
                onUpdateProfile={onUpdateProfile}
              />
            )}
            {activeTab === 'whatsapp' && (
              <WhatsAppTabPanel
                t={t}
                profile={profile}
                onUpdateProfile={onUpdateProfile}
              />
            )}
            {activeTab === 'billing' && (
              <BillingTabPanel
                t={t}
                locale={i18n.language}
                profile={profile}
                onUpdateProfile={onUpdateProfile}
              />
            )}
            {activeTab === 'preferences' && (
              <PreferencesTabPanel
                t={t}
                preferences={preferences}
                onUpdatePreferences={onUpdatePreferences}
              />
            )}
            {activeTab === 'ai' && (
              <AiTabPanel
                t={t}
                preferences={preferences}
                onUpdatePreferences={onUpdatePreferences}
              />
            )}
            {activeTab === 'notifications' && (
              <NotificationsTabPanel
                t={t}
                preferences={preferences}
                onUpdatePreferences={onUpdatePreferences}
              />
            )}
            {activeTab === 'security' && (
              <SecurityTabPanel
                t={t}
                mfaLoading={mfaLoading}
                enrolling={enrolling}
                verifiedFactor={verifiedFactor}
                mfaCode={mfaCode}
                setMfaCode={setMfaCode}
                mfaBusy={mfaBusy}
                mfaError={mfaError}
                onStartEnroll={startEnroll}
                onConfirmEnroll={confirmEnroll}
                onCancelEnroll={cancelEnroll}
                onDisableMfa={disableMfa}
              />
            )}
            {activeTab === 'qrcode' && (
              <QrCodeTabPanel
                t={t}
                locale={i18n.language}
                preferences={preferences}
                onUpdatePreferences={onUpdatePreferences}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ───────────────────────────────────────────────────────────────────────
// Shared building blocks
// ───────────────────────────────────────────────────────────────────────

type TFn = ReturnType<typeof useTranslation>['t']

function SectionHeader({
  title,
  description,
  soonNotice,
}: {
  title: string
  description: string
  soonNotice?: string
}) {
  return (
    <div className="mb-4">
      <h4 className="text-[14px] font-semibold text-[var(--color-text-strong)]">
        {title}
      </h4>
      <p className="mt-1 text-[12px] leading-relaxed text-[var(--color-text-dim)]">
        {description}
      </p>
      {soonNotice && (
        <div className="mt-3 rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2 text-[11px] leading-relaxed text-[var(--color-text-dim)]">
          {soonNotice}
        </div>
      )}
    </div>
  )
}

/** Auto-saving text input. Saves on blur (only when the value actually
 *  changed). Brief inline check icon next to the label confirms the
 *  save; spinner during the network round-trip. Errors surface in
 *  console (silently degraded) — UX is "your edit is being saved" not
 *  "wait for confirmation". */
function Field({
  label,
  hint,
  initialValue,
  onSave,
  placeholder,
  type = 'text',
  multiline,
  maxLength,
  disabled,
}: {
  label: string
  hint?: string
  initialValue: string
  onSave: (value: string) => Promise<void>
  placeholder?: string
  type?: string
  multiline?: boolean
  maxLength?: number
  disabled?: boolean
}) {
  const [value, setValue] = useState(initialValue)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // Re-sync from prop when initialValue changes (e.g., parent refetches
  // the profile after a realtime echo). Avoids stale drafts.
  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  async function commit() {
    if (value === initialValue) return // No change → no save
    setSaving(true)
    try {
      await onSave(value)
      setSaved(true)
      window.setTimeout(() => setSaved(false), 1500)
    } catch (err) {
      console.error('field save failed:', err)
    } finally {
      setSaving(false)
    }
  }

  const labelRow = (
    <span className="mb-1 flex items-center gap-1.5 text-[12px] font-medium text-[var(--color-text-strong)]">
      {label}
      {saving && (
        <Loader2 className="h-3 w-3 animate-spin text-[var(--color-text-dim)]" />
      )}
      {saved && (
        <Check className="h-3 w-3 text-emerald-500" />
      )}
    </span>
  )

  return (
    <label className="block">
      {labelRow}
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
          rows={3}
          className="w-full resize-y rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[12.5px] leading-relaxed text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)] disabled:cursor-not-allowed disabled:opacity-60"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
          className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[12.5px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)] disabled:cursor-not-allowed disabled:opacity-60"
        />
      )}
      {hint && (
        <span className="mt-1 block text-[10.5px] leading-snug text-[var(--color-text-dim)]">
          {hint}
        </span>
      )}
    </label>
  )
}

/** Auto-saving select. Saves on change (not blur — selects close
 *  instantly so blur would race the next render). */
function SelectField({
  label,
  hint,
  value,
  onSave,
  options,
  placeholder,
}: {
  label: string
  hint?: string
  value: string
  onSave: (value: string) => Promise<void>
  options: { value: string; label: string }[]
  placeholder?: string
}) {
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  async function handleChange(v: string) {
    setSaving(true)
    try {
      await onSave(v)
      setSaved(true)
      window.setTimeout(() => setSaved(false), 1500)
    } catch (err) {
      console.error('select save failed:', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <label className="block">
      <span className="mb-1 flex items-center gap-1.5 text-[12px] font-medium text-[var(--color-text-strong)]">
        {label}
        {saving && <Loader2 className="h-3 w-3 animate-spin text-[var(--color-text-dim)]" />}
        {saved && <Check className="h-3 w-3 text-emerald-500" />}
      </span>
      <select
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[12.5px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {hint && (
        <span className="mt-1 block text-[10.5px] leading-snug text-[var(--color-text-dim)]">
          {hint}
        </span>
      )}
    </label>
  )
}

/** Country dropdown (auto-save). ISO 3166-1 alpha-2 codes, names
 *  resolved at runtime via Intl.DisplayNames in the active locale. */
function CountryField({
  label,
  value,
  onSave,
  placeholder,
  locale,
}: {
  label: string
  value: string
  onSave: (value: string) => Promise<void>
  placeholder: string
  locale: string
}) {
  const options = useMemo(() => {
    let formatter: Intl.DisplayNames
    try {
      formatter = new Intl.DisplayNames([locale, 'en'], { type: 'region' })
    } catch {
      formatter = new Intl.DisplayNames(['en'], { type: 'region' })
    }
    return COUNTRY_CODES
      .map((code) => ({ value: code, label: formatter.of(code) ?? code }))
      .sort((a, b) => a.label.localeCompare(b.label, locale))
  }, [locale])

  return (
    <SelectField
      label={label}
      value={value}
      onSave={onSave}
      placeholder={placeholder}
      options={options}
    />
  )
}

/** Sub-section grouping for the long forms (Company / Billing). Keeps
 *  long forms readable by chunking related fields. */
function FieldGroup({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-2xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-4">
      <div className="mb-3 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)]">
        {label}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  )
}

// ───────────────────────────────────────────────────────────────────────
// Tab panels — each field manages its own save lifecycle. Tabs don't
// have local form state any more; the only state lives in the field
// components themselves (controlled inputs + saving / saved flags).
// ───────────────────────────────────────────────────────────────────────

function ProfileTabPanel({
  t,
  user,
  profile,
  fallbackAvatarUrl,
  onUpdateProfile,
  onProfilePatched,
}: {
  t: TFn
  user: User
  profile: UserProfile | null
  fallbackAvatarUrl: string | null
  onUpdateProfile: (patch: Partial<UserProfile>) => Promise<void>
  onProfilePatched?: (patch: { display_name: string | null; avatar_url: string | null }) => void
}) {
  const avatarUrl = profile?.avatar_url ?? fallbackAvatarUrl
  const [uploading, setUploading] = useState(false)
  const [avatarSaved, setAvatarSaved] = useState(false)
  const fileRef = useRef<HTMLInputElement | null>(null)

  // Avatar upload = immediate save (no separate « Save » action). The
  // file picker triggers an upload to storage + an immediate profile
  // update with the new URL. Mirrored to auth.user_metadata so the
  // login screen / sidebar pick it up too.
  async function pickAvatar(file: File) {
    setUploading(true)
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg'
      const path = `avatars/${user.id}-${Date.now()}.${ext}`
      const { error: upErr } = await supabase.storage
        .from('media')
        .upload(path, file, { contentType: file.type, upsert: true })
      if (upErr) throw upErr
      const { data } = supabase.storage.from('media').getPublicUrl(path)
      const newUrl = data.publicUrl
      await onUpdateProfile({ avatar_url: newUrl })
      await supabase.auth.updateUser({ data: { avatar_url: newUrl } })
      onProfilePatched?.({
        display_name: profile?.display_name ?? null,
        avatar_url: newUrl,
      })
      setAvatarSaved(true)
      window.setTimeout(() => setAvatarSaved(false), 1500)
    } catch (err) {
      console.error('avatar upload failed', err)
    } finally {
      setUploading(false)
    }
  }

  // Each text field saves itself on blur. We wrap the onSave callback
  // so it also mirrors display-name changes into auth.user_metadata
  // (needed for the login screen, sidebar header, etc. that read auth
  // state directly rather than going through profilesById).
  async function saveAndMirror(patch: Partial<UserProfile>) {
    await onUpdateProfile(patch)
    if ('display_name' in patch || 'avatar_url' in patch) {
      await supabase.auth.updateUser({
        data: {
          ...(('display_name' in patch) && { display_name: patch.display_name }),
          ...(('avatar_url' in patch) && { avatar_url: patch.avatar_url }),
        },
      })
      onProfilePatched?.({
        display_name: (patch.display_name ?? profile?.display_name) ?? null,
        avatar_url: (patch.avatar_url ?? avatarUrl) ?? null,
      })
    }
  }

  return (
    <div>
      <SectionHeader
        title={t('profile.sections.profile.title')}
        description={t('profile.sections.profile.description')}
      />

      <div className="space-y-4">
        {/* Avatar */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="group relative h-20 w-20 shrink-0 overflow-hidden rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface-2)] shadow-[var(--shadow-soft)]"
            title={t('profile.sections.profile.avatarUpload')}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-[22px] font-semibold text-[var(--color-text-dim)]">
                {initials(
                  profile?.display_name ||
                    `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim() ||
                    user.email ||
                    '?',
                )}
              </span>
            )}
            <span className="absolute inset-0 flex items-center justify-center bg-slate-900/40 opacity-0 transition-opacity group-hover:opacity-100">
              {uploading ? (
                <Loader2 className="h-5 w-5 animate-spin text-white" />
              ) : (
                <Camera className="h-5 w-5 text-white" />
              )}
            </span>
            {avatarSaved && (
              <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white shadow">
                <Check className="h-3 w-3" />
              </span>
            )}
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-medium text-[var(--color-text-strong)]">
              {t('profile.sections.profile.avatarLabel')}
            </div>
            <div className="mt-0.5 text-[11px] text-[var(--color-text-dim)]">
              {t('profile.sections.profile.avatarHint')}
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) pickAvatar(f)
            }}
          />
        </div>

        {/* First + last name */}
        <div className="grid grid-cols-2 gap-3">
          <Field
            label={t('profile.sections.profile.firstNameLabel')}
            initialValue={profile?.first_name ?? ''}
            onSave={(v) => onUpdateProfile({ first_name: v.trim() || null })}
          />
          <Field
            label={t('profile.sections.profile.lastNameLabel')}
            initialValue={profile?.last_name ?? ''}
            onSave={(v) => onUpdateProfile({ last_name: v.trim() || null })}
          />
        </div>

        {/* Display name (optional override) */}
        <Field
          label={t('profile.sections.profile.displayNameLabel')}
          initialValue={profile?.display_name ?? ''}
          onSave={(v) => saveAndMirror({ display_name: v.trim() || null })}
          placeholder={t('profile.sections.profile.displayNamePlaceholder')}
        />

        {/* Phone */}
        <Field
          label={t('profile.sections.profile.phoneLabel')}
          initialValue={profile?.phone ?? ''}
          onSave={(v) => onUpdateProfile({ phone: v.trim() || null })}
          placeholder={t('profile.sections.profile.phonePlaceholder')}
          type="tel"
        />

        {/* Email (read-only) */}
        <div>
          <span className="mb-1 block text-[12px] font-medium text-[var(--color-text-strong)]">
            {t('profile.sections.profile.emailLabel')}
          </span>
          <div className="rounded-xl bg-[var(--color-surface-2)] px-3 py-2 text-[12.5px] text-[var(--color-text-dim)]">
            {user.email}
          </div>
        </div>
      </div>
    </div>
  )
}

function CompanyTabPanel({
  t,
  locale,
  profile,
  onUpdateProfile,
}: {
  t: TFn
  locale: string
  profile: UserProfile | null
  onUpdateProfile: (patch: Partial<UserProfile>) => Promise<void>
}) {
  // Tiny helper to dedupe the "trim or null then save" boilerplate.
  const save =
    <K extends keyof UserProfile>(key: K) =>
    (v: string) =>
      onUpdateProfile({ [key]: v.trim() || null } as Partial<UserProfile>)

  return (
    <div>
      <SectionHeader
        title={t('profile.sections.company.title')}
        description={t('profile.sections.company.description')}
      />

      <div className="space-y-5">
        <FieldGroup label={t('profile.sections.company.groupIdentity')}>
          <Field
            label={t('profile.sections.company.legalNameLabel')}
            initialValue={profile?.company_legal_name ?? ''}
            onSave={save('company_legal_name')}
            placeholder={t('profile.sections.company.legalNamePlaceholder')}
          />
          <Field
            label={t('profile.sections.company.tradingNameLabel')}
            initialValue={profile?.company_trading_name ?? ''}
            onSave={save('company_trading_name')}
            placeholder={t('profile.sections.company.tradingNamePlaceholder')}
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label={t('profile.sections.company.registrationNumberLabel')}
              initialValue={profile?.company_registration_number ?? ''}
              onSave={save('company_registration_number')}
              placeholder={t('profile.sections.company.registrationNumberPlaceholder')}
            />
            <Field
              label={t('profile.sections.company.vatNumberLabel')}
              initialValue={profile?.company_vat_number ?? ''}
              onSave={save('company_vat_number')}
              placeholder={t('profile.sections.company.vatNumberPlaceholder')}
            />
          </div>
        </FieldGroup>

        <FieldGroup label={t('profile.sections.company.groupAddress')}>
          <Field
            label={t('profile.sections.company.addressLine1Label')}
            initialValue={profile?.company_address_line1 ?? ''}
            onSave={save('company_address_line1')}
            placeholder={t('profile.sections.company.addressLine1Placeholder')}
          />
          <Field
            label={t('profile.sections.company.addressLine2Label')}
            initialValue={profile?.company_address_line2 ?? ''}
            onSave={save('company_address_line2')}
            placeholder={t('profile.sections.company.addressLine2Placeholder')}
          />
          <div className="grid grid-cols-3 gap-3">
            <Field
              label={t('profile.sections.company.postalCodeLabel')}
              initialValue={profile?.company_postal_code ?? ''}
              onSave={save('company_postal_code')}
            />
            <Field
              label={t('profile.sections.company.cityLabel')}
              initialValue={profile?.company_city ?? ''}
              onSave={save('company_city')}
            />
            <CountryField
              label={t('profile.sections.company.countryLabel')}
              value={profile?.company_country ?? ''}
              onSave={save('company_country')}
              placeholder={t('profile.sections.company.countryPlaceholder')}
              locale={locale}
            />
          </div>
        </FieldGroup>

        <FieldGroup label={t('profile.sections.company.groupContact')}>
          <Field
            label={t('profile.sections.company.websiteLabel')}
            initialValue={profile?.company_website ?? ''}
            onSave={save('company_website')}
            placeholder={t('profile.sections.company.websitePlaceholder')}
            type="url"
          />
          <Field
            label={t('profile.sections.company.emailLabel')}
            initialValue={profile?.company_email ?? ''}
            onSave={save('company_email')}
            placeholder={t('profile.sections.company.emailPlaceholder')}
            type="email"
          />
        </FieldGroup>
      </div>
    </div>
  )
}

function WhatsAppTabPanel({
  t,
  profile,
  onUpdateProfile,
}: {
  t: TFn
  profile: UserProfile | null
  onUpdateProfile: (patch: Partial<UserProfile>) => Promise<void>
}) {
  const save =
    <K extends keyof UserProfile>(key: K) =>
    (v: string) =>
      onUpdateProfile({ [key]: v.trim() || null } as Partial<UserProfile>)

  const categoryKeys = [
    'AUTO', 'BEAUTY', 'APPAREL', 'EDU', 'ENTERTAIN', 'EVENT_PLAN',
    'FINANCE', 'GROCERY', 'GOVT', 'HOTEL', 'HEALTH', 'NONPROFIT',
    'PROF_SERVICES', 'RETAIL', 'TRAVEL', 'RESTAURANT', 'OTHER',
  ]

  return (
    <div>
      <SectionHeader
        title={t('profile.sections.whatsapp.title')}
        description={t('profile.sections.whatsapp.description')}
        soonNotice={t('profile.sections.whatsapp.soonNotice')}
      />

      <div className="space-y-4">
        <Field
          label={t('profile.sections.whatsapp.displayNameLabel')}
          hint={t('profile.sections.whatsapp.displayNameHint')}
          initialValue={profile?.whatsapp_display_name ?? ''}
          onSave={save('whatsapp_display_name')}
          placeholder={t('profile.sections.whatsapp.displayNamePlaceholder')}
          maxLength={25}
        />

        <Field
          label={t('profile.sections.whatsapp.aboutLabel')}
          hint={t('profile.sections.whatsapp.aboutHint')}
          initialValue={profile?.whatsapp_about ?? ''}
          onSave={save('whatsapp_about')}
          placeholder={t('profile.sections.whatsapp.aboutPlaceholder')}
          maxLength={139}
          multiline
        />

        <SelectField
          label={t('profile.sections.whatsapp.categoryLabel')}
          hint={t('profile.sections.whatsapp.categoryHint')}
          value={profile?.whatsapp_business_category ?? ''}
          onSave={save('whatsapp_business_category')}
          placeholder="—"
          options={categoryKeys.map((k) => ({
            value: k,
            label: t(`profile.sections.whatsapp.categories.${k}`),
          }))}
        />
      </div>
    </div>
  )
}

function BillingTabPanel({
  t,
  locale,
  profile,
  onUpdateProfile,
}: {
  t: TFn
  locale: string
  profile: UserProfile | null
  onUpdateProfile: (patch: Partial<UserProfile>) => Promise<void>
}) {
  const save =
    <K extends keyof UserProfile>(key: K) =>
    (v: string) =>
      onUpdateProfile({ [key]: v.trim() || null } as Partial<UserProfile>)

  // « Use company address » helper — copies the company fields into the
  // billing columns in a single save. Honest pattern: the user can edit
  // after, no hidden "linked" state to manage.
  const [copying, setCopying] = useState(false)
  const [copied, setCopied] = useState(false)
  async function copyFromCompany() {
    if (!profile) return
    setCopying(true)
    try {
      await onUpdateProfile({
        billing_address_line1: profile.company_address_line1,
        billing_address_line2: profile.company_address_line2,
        billing_postal_code: profile.company_postal_code,
        billing_city: profile.company_city,
        billing_country: profile.company_country,
        billing_vat_number: profile.company_vat_number,
      })
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch (err) {
      console.error('copy from company failed', err)
    } finally {
      setCopying(false)
    }
  }

  return (
    <div>
      <SectionHeader
        title={t('profile.sections.billing.title')}
        description={t('profile.sections.billing.description')}
        soonNotice={t('profile.sections.billing.soonNotice')}
      />

      <div className="space-y-4">
        <button
          type="button"
          onClick={copyFromCompany}
          disabled={!profile?.company_address_line1 || copying}
          className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[12px] font-medium text-[var(--color-text)] transition-colors hover:border-[var(--color-link)] hover:text-[var(--color-link)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {copying ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : copied ? (
            <Check className="h-3.5 w-3.5 text-emerald-500" />
          ) : (
            <Building2 className="h-3.5 w-3.5" />
          )}
          {t('profile.sections.billing.sameAsCompany')}
        </button>

        <Field
          label={t('profile.sections.billing.addressLine1Label')}
          initialValue={profile?.billing_address_line1 ?? ''}
          onSave={save('billing_address_line1')}
        />
        <Field
          label={t('profile.sections.billing.addressLine2Label')}
          initialValue={profile?.billing_address_line2 ?? ''}
          onSave={save('billing_address_line2')}
        />
        <div className="grid grid-cols-3 gap-3">
          <Field
            label={t('profile.sections.billing.postalCodeLabel')}
            initialValue={profile?.billing_postal_code ?? ''}
            onSave={save('billing_postal_code')}
          />
          <Field
            label={t('profile.sections.billing.cityLabel')}
            initialValue={profile?.billing_city ?? ''}
            onSave={save('billing_city')}
          />
          <CountryField
            label={t('profile.sections.billing.countryLabel')}
            value={profile?.billing_country ?? ''}
            onSave={save('billing_country')}
            placeholder={t('profile.sections.company.countryPlaceholder')}
            locale={locale}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label={t('profile.sections.billing.vatNumberLabel')}
            initialValue={profile?.billing_vat_number ?? ''}
            onSave={save('billing_vat_number')}
            placeholder="FR12345678901"
          />
          <Field
            label={t('profile.sections.billing.emailLabel')}
            initialValue={profile?.billing_email ?? ''}
            onSave={save('billing_email')}
            placeholder={t('profile.sections.billing.emailPlaceholder')}
            type="email"
          />
        </div>
      </div>
    </div>
  )
}

function PreferencesTabPanel({
  t,
  preferences,
  onUpdatePreferences,
}: {
  t: TFn
  preferences: UserPreferences
  onUpdatePreferences: (patch: Partial<UserPreferences>) => void | Promise<void>
}) {
  const currentLocale = preferences.locale ?? ''
  const currentSpeed = preferences.audio_speed ?? DEFAULT_AUDIO_SPEED
  const currentTheme = preferences.theme ?? DEFAULT_THEME

  return (
    <div>
      <SectionHeader
        title={t('profile.sections.preferences.title')}
        description={t('profile.sections.preferences.description')}
      />

      <div className="space-y-5">
        <div>
          <label className="flex items-center gap-1.5 text-[12px] font-medium text-[var(--color-text-strong)]">
            <Languages className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
            {t('profile.sections.preferences.languageLabel')}
          </label>
          <p className="mt-1 mb-2 text-[11px] leading-snug text-[var(--color-text-dim)]">
            {t('profile.sections.preferences.languageHint')}
          </p>
          <select
            value={currentLocale}
            onChange={(e) =>
              onUpdatePreferences({ locale: e.target.value || undefined })
            }
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
          >
            <option value="">{t('profile.sections.preferences.languageAuto')}</option>
            {SUPPORTED_LOCALES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="flex items-center gap-1.5 text-[12px] font-medium text-[var(--color-text-strong)]">
            <Palette className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
            {t('profile.sections.preferences.themeLabel')}
          </div>
          <p className="mt-1 mb-2 text-[11px] leading-snug text-[var(--color-text-dim)]">
            {t('profile.sections.preferences.themeHint')}
          </p>
          <div className="grid grid-cols-3 gap-1.5">
            {THEMES.map((tm) => {
              const active = currentTheme === tm.key
              return (
                <button
                  key={tm.key}
                  type="button"
                  onClick={() => onUpdatePreferences({ theme: tm.key })}
                  title={tm.description}
                  className={cn(
                    'overflow-hidden rounded-xl border bg-[var(--color-surface)] p-1.5 text-left transition-all',
                    active
                      ? 'border-[var(--color-accent)] ring-1 ring-[var(--color-accent)]'
                      : 'border-[var(--color-border-soft)] hover:border-[var(--color-border)]',
                  )}
                >
                  <div
                    className="flex h-9 items-end justify-end gap-0.5 rounded-md p-1"
                    style={{ background: tm.bg }}
                  >
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{
                        background: tm.surface,
                        border: '1px solid rgba(0,0,0,0.05)',
                      }}
                    />
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ background: tm.accent }}
                    />
                  </div>
                  <div
                    className="mt-1 truncate text-[10.5px] font-medium"
                    style={{ color: 'var(--color-text-strong)' }}
                  >
                    {tm.label}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1.5 text-[12px] font-medium text-[var(--color-text-strong)]">
            <Gauge className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
            {t('profile.sections.preferences.audioSpeedLabel')}
          </div>
          <p className="mt-1 mb-2 text-[11px] leading-snug text-[var(--color-text-dim)]">
            {t('profile.sections.preferences.audioSpeedHint')}
          </p>
          <div className="flex gap-1.5">
            {AUDIO_SPEEDS.map((rate) => {
              const active = currentSpeed === rate
              return (
                <button
                  key={rate}
                  type="button"
                  onClick={() => onUpdatePreferences({ audio_speed: rate })}
                  className={cn(
                    'flex-1 rounded-xl px-2.5 py-2 text-[12px] font-semibold tabular-nums transition-colors',
                    active
                      ? 'bg-[var(--color-accent)] text-white'
                      : 'bg-[var(--color-surface-2)] text-[var(--color-text-dim)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]',
                  )}
                >
                  {rate}×
                </button>
              )
            })}
          </div>
        </div>

        {/* Where new cards land in their list (manual + ingest). */}
        <div>
          <div className="flex items-center gap-1.5 text-[12px] font-medium text-[var(--color-text-strong)]">
            <ArrowDownWideNarrow className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
            {t('profile.sections.preferences.newCardPositionLabel')}
          </div>
          <p className="mt-1 mb-2 text-[11px] leading-snug text-[var(--color-text-dim)]">
            {t('profile.sections.preferences.newCardPositionHint')}
          </p>
          <div className="flex gap-1.5">
            {(['top', 'bottom'] as const).map((pos) => {
              const active = (preferences.new_card_position ?? 'top') === pos
              return (
                <button
                  key={pos}
                  type="button"
                  onClick={() => onUpdatePreferences({ new_card_position: pos })}
                  className={cn(
                    'flex-1 rounded-xl px-2.5 py-2 text-[12px] font-semibold transition-colors',
                    active
                      ? 'bg-[var(--color-accent)] text-white'
                      : 'bg-[var(--color-surface-2)] text-[var(--color-text-dim)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]',
                  )}
                >
                  {t(`profile.sections.preferences.newCardPosition_${pos}`)}
                </button>
              )
            })}
          </div>
        </div>

        {/* How many days a card stays flagged "new" while unseen. */}
        <div>
          <div className="flex items-center gap-1.5 text-[12px] font-medium text-[var(--color-text-strong)]">
            <SettingsIcon className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
            {t('profile.sections.preferences.newCardDaysLabel')}
          </div>
          <p className="mt-1 mb-2 text-[11px] leading-snug text-[var(--color-text-dim)]">
            {t('profile.sections.preferences.newCardDaysHint')}
          </p>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={90}
              value={preferences.new_card_days ?? 7}
              onChange={(e) => {
                const v = Math.max(1, Math.min(90, Math.round(Number(e.target.value) || 7)))
                onUpdatePreferences({ new_card_days: v })
              }}
              className="w-20 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-2 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
            />
            <span className="text-[12px] text-[var(--color-text-dim)]">
              {t('profile.sections.preferences.newCardDaysUnit')}
            </span>
          </div>
        </div>

        {/* What clears a card's "new" flag — open only, or open + hover. */}
        <div>
          <div className="flex items-center gap-1.5 text-[12px] font-medium text-[var(--color-text-strong)]">
            <SettingsIcon className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
            {t('profile.sections.preferences.newCardDismissLabel')}
          </div>
          <p className="mt-1 mb-2 text-[11px] leading-snug text-[var(--color-text-dim)]">
            {t('profile.sections.preferences.newCardDismissHint')}
          </p>
          <div className="flex gap-1.5">
            {(['hover_or_open', 'open_only'] as const).map((mode) => {
              const active = (preferences.new_card_dismiss ?? 'hover_or_open') === mode
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => onUpdatePreferences({ new_card_dismiss: mode })}
                  className={cn(
                    'flex-1 rounded-xl px-2.5 py-2 text-[12px] font-semibold transition-colors',
                    active
                      ? 'bg-[var(--color-accent)] text-white'
                      : 'bg-[var(--color-surface-2)] text-[var(--color-text-dim)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]',
                  )}
                >
                  {t(`profile.sections.preferences.newCardDismiss_${mode}`)}
                </button>
              )
            })}
          </div>
        </div>

        {/* Toolbar density — show or hide text labels next to icons. */}
        <div>
          <div className="flex items-center gap-1.5 text-[12px] font-medium text-[var(--color-text-strong)]">
            <Captions className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
            {t('profile.sections.preferences.toolbarLabelsLabel')}
          </div>
          <p className="mt-1 mb-2 text-[11px] leading-snug text-[var(--color-text-dim)]">
            {t('profile.sections.preferences.toolbarLabelsHint')}
          </p>
          <div className="flex gap-1.5">
            {([true, false] as const).map((show) => {
              const active = (preferences.toolbar_labels !== false) === show
              return (
                <button
                  key={String(show)}
                  type="button"
                  onClick={() => onUpdatePreferences({ toolbar_labels: show })}
                  className={cn(
                    'flex-1 rounded-xl px-2.5 py-2 text-[12px] font-semibold transition-colors',
                    active
                      ? 'bg-[var(--color-accent)] text-white'
                      : 'bg-[var(--color-surface-2)] text-[var(--color-text-dim)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]',
                  )}
                >
                  {t(
                    show
                      ? 'profile.sections.preferences.toolbarLabels_show'
                      : 'profile.sections.preferences.toolbarLabels_hide',
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Master notification-sound switch. When off, no per-project
            sound ever plays. */}
        <div>
          <div className="flex items-center gap-1.5 text-[12px] font-medium text-[var(--color-text-strong)]">
            <Volume2 className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
            {t('profile.sections.preferences.notifySoundLabel')}
          </div>
          <p className="mt-1 mb-2 text-[11px] leading-snug text-[var(--color-text-dim)]">
            {t('profile.sections.preferences.notifySoundHint')}
          </p>
          <div className="flex gap-1.5">
            {([true, false] as const).map((on) => {
              const active = (preferences.notify_sound_enabled !== false) === on
              return (
                <button
                  key={String(on)}
                  type="button"
                  onClick={() =>
                    onUpdatePreferences({ notify_sound_enabled: on })
                  }
                  className={cn(
                    'flex-1 rounded-xl px-2.5 py-2 text-[12px] font-semibold transition-colors',
                    active
                      ? 'bg-[var(--color-accent)] text-white'
                      : 'bg-[var(--color-surface-2)] text-[var(--color-text-dim)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]',
                  )}
                >
                  {t(
                    on
                      ? 'profile.sections.preferences.notifySound_on'
                      : 'profile.sections.preferences.notifySound_off',
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

function AiTabPanel({
  t,
  preferences,
  onUpdatePreferences,
}: {
  t: TFn
  preferences: UserPreferences
  onUpdatePreferences: (patch: Partial<UserPreferences>) => void | Promise<void>
}) {
  return (
    <div>
      <SectionHeader
        title={t('profile.sections.ai.title')}
        description={t('profile.sections.ai.description')}
      />

      <div className="space-y-2.5">
        <AiToggle
          checked={preferences.auto_transcribe === true}
          onChange={(v) => onUpdatePreferences({ auto_transcribe: v })}
          icon={<Captions className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />}
          label={t('profile.sections.ai.autoTranscribeLabel')}
          hint={t('profile.sections.ai.autoTranscribeHint')}
        />
        <AiToggle
          checked={preferences.ai_detect_question !== false}
          onChange={(v) => onUpdatePreferences({ ai_detect_question: v })}
          icon={<HelpCircle className="h-3.5 w-3.5 text-amber-500" />}
          label={t('profile.sections.ai.detectQuestionLabel')}
          hint={t('profile.sections.ai.detectQuestionHint')}
        />
        <AiToggle
          checked={preferences.ai_detect_date !== false}
          onChange={(v) => onUpdatePreferences({ ai_detect_date: v })}
          icon={<CalendarDays className="h-3.5 w-3.5 text-sky-500" />}
          label={t('profile.sections.ai.detectDateLabel')}
          hint={t('profile.sections.ai.detectDateHint')}
        />
        <AiToggle
          checked={preferences.ai_detect_urgency !== false}
          onChange={(v) => onUpdatePreferences({ ai_detect_urgency: v })}
          icon={<Flame className="h-3.5 w-3.5 text-rose-500" />}
          label={t('profile.sections.ai.detectUrgencyLabel')}
          hint={t('profile.sections.ai.detectUrgencyHint')}
        />
        <AiToggle
          checked={preferences.ai_auto_priority !== false}
          onChange={(v) => onUpdatePreferences({ ai_auto_priority: v })}
          disabled={preferences.ai_detect_urgency === false}
          icon={<TrendingUp className="h-3.5 w-3.5 text-rose-500" />}
          label={t('profile.sections.ai.autoPriorityLabel')}
          hint={t('profile.sections.ai.autoPriorityHint')}
        />
      </div>
    </div>
  )
}

function AiToggle({
  checked,
  onChange,
  disabled,
  icon,
  label,
  hint,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  icon: React.ReactNode
  label: string
  hint: string
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-2.5 rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] px-3 py-2.5 transition-colors',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'hover:border-[var(--color-border)]',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[var(--color-accent)] disabled:cursor-not-allowed"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[12.5px] font-medium text-[var(--color-text-strong)]">
          {icon}
          {label}
        </div>
        <div className="mt-0.5 text-[11px] leading-snug text-[var(--color-text-dim)]">
          {hint}
        </div>
      </div>
    </label>
  )
}

function NotificationsTabPanel({
  t,
  preferences,
  onUpdatePreferences,
}: {
  t: TFn
  preferences: UserPreferences
  onUpdatePreferences: (patch: Partial<UserPreferences>) => void | Promise<void>
}) {
  // Default-true for every per-event toggle, default-false for the
  // master switch override and includeOwnCards. The dispatcher applies
  // the same defaults server-side so unchanged users keep getting
  // notifs after this ships.
  const notif = preferences.notifications ?? {}
  const enabled = notif.enabled !== false
  const get = (k: keyof NonNullable<UserPreferences['notifications']>) =>
    notif[k] !== false
  const patch = (k: keyof NonNullable<UserPreferences['notifications']>, v: boolean) => {
    onUpdatePreferences({ notifications: { ...notif, [k]: v } })
  }

  return (
    <div>
      <SectionHeader
        title={t('profile.sections.notifications.title')}
        description={t('profile.sections.notifications.description')}
      />

      {/* Master switch — disables every per-event toggle visually. Full
          width to give it visual weight as the "kill switch". */}
      <div className="mb-5">
        <AiToggle
          checked={enabled}
          onChange={(v) => patch('enabled', v)}
          icon={<Mail className="h-3.5 w-3.5 text-[var(--color-accent)]" />}
          label={t('profile.sections.notifications.masterLabel')}
          hint={t('profile.sections.notifications.masterHint')}
        />
      </div>

      {/* 2-column grid grouped by domain. On narrow widths (< lg) we
          collapse to one column. The whole grid gets pointer-events-none
          + opacity when the master switch is off — visual cue that the
          per-event toggles are inert. */}
      <div
        className={cn(
          'grid grid-cols-1 gap-x-6 gap-y-5 lg:grid-cols-2',
          !enabled && 'pointer-events-none opacity-50',
        )}
      >
        <NotifGroup label={t('profile.sections.notifications.groupProjects')}>
          <AiToggle
            checked={get('projectAdded')}
            onChange={(v) => patch('projectAdded', v)}
            icon={<UserPlusIcon className="h-3.5 w-3.5 text-emerald-500" />}
            label={t('profile.sections.notifications.projectAddedLabel')}
            hint={t('profile.sections.notifications.projectAddedHint')}
          />
          <AiToggle
            checked={get('projectRemoved')}
            onChange={(v) => patch('projectRemoved', v)}
            icon={<LogOutIcon className="h-3.5 w-3.5 text-rose-500" />}
            label={t('profile.sections.notifications.projectRemovedLabel')}
            hint={t('profile.sections.notifications.projectRemovedHint')}
          />
        </NotifGroup>

        <NotifGroup label={t('profile.sections.notifications.groupCardSubs')}>
          <AiToggle
            checked={get('cardAdded')}
            onChange={(v) => patch('cardAdded', v)}
            icon={<Inbox className="h-3.5 w-3.5 text-sky-500" />}
            label={t('profile.sections.notifications.cardAddedLabel')}
            hint={t('profile.sections.notifications.cardAddedHint')}
          />
          <AiToggle
            checked={get('cardMember')}
            onChange={(v) => patch('cardMember', v)}
            icon={<UserPlusIcon className="h-3.5 w-3.5 text-teal-500" />}
            label={t('profile.sections.notifications.cardMemberLabel')}
            hint={t('profile.sections.notifications.cardMemberHint')}
          />
        </NotifGroup>

        <NotifGroup label={t('profile.sections.notifications.groupCardActivity')}>
          <AiToggle
            checked={get('cardComment')}
            onChange={(v) => patch('cardComment', v)}
            icon={<MessageSquare className="h-3.5 w-3.5 text-violet-500" />}
            label={t('profile.sections.notifications.cardCommentLabel')}
            hint={t('profile.sections.notifications.cardCommentHint')}
          />
          <AiToggle
            checked={get('cardStatus')}
            onChange={(v) => patch('cardStatus', v)}
            icon={<ArrowRightLeftIcon className="h-3.5 w-3.5 text-amber-500" />}
            label={t('profile.sections.notifications.cardStatusLabel')}
            hint={t('profile.sections.notifications.cardStatusHint')}
          />
          <AiToggle
            checked={get('cardPriority')}
            onChange={(v) => patch('cardPriority', v)}
            icon={<Flag className="h-3.5 w-3.5 text-orange-500" />}
            label={t('profile.sections.notifications.cardPriorityLabel')}
            hint={t('profile.sections.notifications.cardPriorityHint')}
          />
        </NotifGroup>

        <NotifGroup label={t('profile.sections.notifications.groupCardLifecycle')}>
          <AiToggle
            checked={get('cardArchived')}
            onChange={(v) => patch('cardArchived', v)}
            icon={<ArchiveIcon className="h-3.5 w-3.5 text-slate-500" />}
            label={t('profile.sections.notifications.cardArchivedLabel')}
            hint={t('profile.sections.notifications.cardArchivedHint')}
          />
          <AiToggle
            checked={get('cardDeleted')}
            onChange={(v) => patch('cardDeleted', v)}
            icon={<Trash2 className="h-3.5 w-3.5 text-rose-500" />}
            label={t('profile.sections.notifications.cardDeletedLabel')}
            hint={t('profile.sections.notifications.cardDeletedHint')}
          />
        </NotifGroup>
      </div>

      {/* includeOwnCards — full width separator at the bottom. Default
          off, hence different default-state lookup. */}
      <div className="mt-6 border-t border-[var(--color-border-soft)] pt-5">
        <AiToggle
          checked={notif.includeOwnCards === true}
          onChange={(v) => patch('includeOwnCards', v)}
          icon={<Bell className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />}
          label={t('profile.sections.notifications.includeOwnLabel')}
          hint={t('profile.sections.notifications.includeOwnHint')}
        />
      </div>
    </div>
  )
}

/** A labelled cluster of AiToggle rows. Keeps the notifications grid
 *  scannable — header + items below, with the items in a tight stack. */
function NotifGroup({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-2">
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[var(--color-text-dim)]">
        {label}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  )
}

function SecurityTabPanel({
  t,
  mfaLoading,
  enrolling,
  verifiedFactor,
  mfaCode,
  setMfaCode,
  mfaBusy,
  mfaError,
  onStartEnroll,
  onConfirmEnroll,
  onCancelEnroll,
  onDisableMfa,
}: {
  t: TFn
  mfaLoading: boolean
  enrolling: { id: string; qr: string; secret: string } | null
  verifiedFactor: { id: string; status: string } | undefined
  mfaCode: string
  setMfaCode: (v: string) => void
  mfaBusy: boolean
  mfaError: string | null
  onStartEnroll: () => Promise<void>
  onConfirmEnroll: () => Promise<void>
  onCancelEnroll: () => Promise<void>
  onDisableMfa: (id: string) => Promise<void>
}) {
  return (
    <div>
      <SectionHeader
        title={t('profile.sections.security.title')}
        description={t('profile.sections.security.description')}
      />

      <div className="rounded-2xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-4">
        <div className="mb-3 flex items-center gap-1.5 text-[12px] font-semibold text-[var(--color-text-strong)]">
          <ShieldCheck className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
          {t('profile.sections.security.twoFactorTitle')}
        </div>

        {mfaLoading ? (
          <div className="flex items-center gap-2 text-[12px] text-[var(--color-text-dim)]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t('common.loading')}
          </div>
        ) : enrolling ? (
          <div className="space-y-3">
            <p className="text-[11.5px] leading-relaxed text-[var(--color-text)]">
              {t('profile.sections.security.twoFactorScanHint')}
            </p>
            <div className="flex items-start gap-3">
              <img
                src={enrolling.qr}
                alt="2FA QR code"
                className="h-32 w-32 shrink-0 rounded-xl border border-[var(--color-border-soft)] bg-white"
              />
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wide text-[var(--color-text-dim)]">
                  {t('profile.sections.security.twoFactorManualKey')}
                </div>
                <div className="mt-0.5 break-all font-mono text-[11px] text-[var(--color-text)]">
                  {enrolling.secret}
                </div>
              </div>
            </div>
            <input
              value={mfaCode}
              onChange={(e) =>
                setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder={t('profile.sections.security.twoFactorCodePlaceholder')}
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-center text-[16px] font-semibold tracking-[0.25em] text-[var(--color-text-strong)] outline-none focus:border-[var(--color-link)]"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onConfirmEnroll}
                disabled={mfaBusy || mfaCode.length < 6}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[var(--color-accent)] px-3 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {mfaBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('profile.sections.security.twoFactorVerify')}
              </button>
              <button
                type="button"
                onClick={onCancelEnroll}
                disabled={mfaBusy}
                className="rounded-xl px-3 py-2 text-[12.5px] text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-3)]"
              >
                {t('profile.sections.security.twoFactorCancel')}
              </button>
            </div>
          </div>
        ) : verifiedFactor ? (
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-emerald-700">
              <ShieldCheck className="h-4 w-4" />
              {t('profile.sections.security.twoFactorEnabledLabel')}
            </span>
            <button
              type="button"
              onClick={() => onDisableMfa(verifiedFactor.id)}
              disabled={mfaBusy}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-2.5 py-1.5 text-[11.5px] font-medium text-[var(--color-text-dim)] transition-colors hover:border-[var(--color-danger)] hover:text-[var(--color-danger)] disabled:opacity-60"
            >
              <ShieldOff className="h-3.5 w-3.5" />
              {t('profile.sections.security.twoFactorDisable')}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <span className="text-[12px] text-[var(--color-text-dim)]">
              {t('profile.sections.security.twoFactorDisabledLabel')}
            </span>
            <button
              type="button"
              onClick={onStartEnroll}
              disabled={mfaBusy}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-3 py-1.5 text-[11.5px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:opacity-60"
            >
              {mfaBusy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {t('profile.sections.security.twoFactorEnable')}
            </button>
          </div>
        )}

        {mfaError && (
          <div className="mt-3 rounded-xl border border-red-200 bg-[var(--color-danger-bg)] px-3 py-2 text-[11px] text-[var(--color-danger)]">
            {mfaError}
          </div>
        )}
      </div>
    </div>
  )
}

/** QR code / invite link panel. The user enters their WhatsApp Business
 *  number + an optional pre-filled first message; we build a wa.me link
 *  and render a downloadable QR code. They can paste the link (or send the
 *  QR image) to their contacts from the old account / by email so people
 *  migrate to the new WhatsApp Business number in one tap. */
function QrCodeTabPanel({
  t,
  locale,
  preferences,
  onUpdatePreferences,
}: {
  t: TFn
  locale: string
  preferences: UserPreferences
  onUpdatePreferences: (patch: Partial<UserPreferences>) => void | Promise<void>
}) {
  const [number, setNumber] = useState(preferences.wa_invite_number ?? '')
  const [tone, setTone] = useState<ReplyTone>(
    preferences.wa_invite_tone ?? 'vous_pro',
  )
  // The stored message keeps a {{link}} token; we bake the real link in for
  // display / copy. Editing replaces the stored text with the baked version.
  const [message, setMessage] = useState(preferences.wa_invite_message ?? '')
  const [generating, setGenerating] = useState(false)
  const [copied, setCopied] = useState<'link' | 'msg' | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  const rawDigits = number.replace(/[^\d]/g, '')
  // wa.me needs the FULL international number (country code, no leading 0).
  // A leading 0 = national trunk prefix → country code missing → WhatsApp
  // can't resolve it. A leading 00 = international prefix → drop it.
  const digits = rawDigits.startsWith('00') ? rawDigits.slice(2) : rawDigits
  const looksNational = digits.startsWith('0')
  const validNumber = digits.length >= 8 && !looksNational
  const link = validNumber ? `https://wa.me/${digits}` : ''
  const linkPlaceholder = locale === 'fr' ? '« votre lien »' : '« your link »'
  const displayMessage = message.replace(/\{\{\s*link\s*\}\}/g, link || linkPlaceholder)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    if (!link) {
      canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height)
      return
    }
    QRCode.toCanvas(canvas, link, { width: 220, margin: 1 }, () => {})
  }, [link])

  async function regenerate(nextTone: ReplyTone) {
    setGenerating(true)
    try {
      const { data, error } = await supabase.functions.invoke('generate-invite', {
        body: { tone: nextTone, locale },
      })
      if (error) throw error
      const msg = (data as { message?: string }).message ?? ''
      if (msg) {
        setMessage(msg)
        void onUpdatePreferences({ wa_invite_message: msg })
      }
    } catch {
      /* silent — the message is a nicety */
    } finally {
      setGenerating(false)
    }
  }

  // First open with no saved message → generate one for the default tone.
  useEffect(() => {
    if (!message.trim()) void regenerate(tone)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function changeTone(next: ReplyTone) {
    if (next === tone) return
    setTone(next)
    void onUpdatePreferences({ wa_invite_tone: next })
    void regenerate(next)
  }

  function persistNumber() {
    if ((preferences.wa_invite_number ?? '') !== number) {
      void onUpdatePreferences({ wa_invite_number: number })
    }
  }
  function persistMessage() {
    if ((preferences.wa_invite_message ?? '') !== message) {
      void onUpdatePreferences({ wa_invite_message: message })
    }
  }

  async function copyText(kind: 'link' | 'msg', text: string) {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopied(kind)
      window.setTimeout(() => setCopied(null), 1500)
    } catch {
      /* clipboard unavailable */
    }
  }

  function downloadQr() {
    const canvas = canvasRef.current
    if (!canvas || !link) return
    const a = document.createElement('a')
    a.href = canvas.toDataURL('image/png')
    a.download = 'whatsapp-qr.png'
    a.click()
  }

  return (
    <div>
      <SectionHeader
        title={t('profile.sections.qrcode.title')}
        description={t('profile.sections.qrcode.description')}
      />

      <div className="space-y-5">
        {/* WhatsApp Business number */}
        <label className="block">
          <span className="mb-1 block text-[12px] font-medium text-[var(--color-text-strong)]">
            {t('profile.sections.qrcode.numberLabel')}
          </span>
          <input
            type="tel"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            onBlur={persistNumber}
            placeholder="33 6 12 34 56 78"
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[12.5px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
          />
          <span className="mt-1 block text-[10.5px] leading-snug text-[var(--color-text-dim)]">
            {t('profile.sections.qrcode.numberHint')}
          </span>
        </label>

        {/* Tone slider — drives the AI generation of the message */}
        <div>
          <div className="mb-1 flex items-center gap-1.5 text-[12px] font-medium text-[var(--color-text-strong)]">
            <Sparkles className="h-3.5 w-3.5 text-[var(--color-text-dim)]" />
            {t('profile.sections.qrcode.toneLabel')}
          </div>
          <p className="mt-1 mb-2 text-[11px] leading-snug text-[var(--color-text-dim)]">
            {t('profile.sections.qrcode.toneHint')}
          </p>
          <ToneSlider value={tone} disabled={generating} onChange={changeTone} t={t} />
        </div>

        {/* Generated announcement message (editable) */}
        <div>
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="text-[12px] font-medium text-[var(--color-text-strong)]">
              {t('profile.sections.qrcode.messageLabel')}
            </span>
            <button
              type="button"
              onClick={() => regenerate(tone)}
              disabled={generating}
              className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-text-dim)] transition-colors hover:border-[var(--color-link)] hover:text-[var(--color-link)] disabled:opacity-50"
            >
              {generating ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              {t('profile.sections.qrcode.regenerate')}
            </button>
          </div>
          <textarea
            value={displayMessage}
            onChange={(e) => setMessage(e.target.value)}
            onBlur={persistMessage}
            rows={5}
            maxLength={1500}
            placeholder={t('profile.sections.qrcode.messageHint')}
            className="w-full resize-y rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[12.5px] leading-relaxed text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
          />
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => copyText('msg', displayMessage)}
              disabled={!displayMessage.trim()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[11.5px] font-medium text-[var(--color-text)] transition-colors hover:border-[var(--color-link)] disabled:opacity-50"
            >
              {copied === 'msg' ? (
                <Check className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              {t('profile.sections.qrcode.copyMessage')}
            </button>
            {!digits && (
              <span className="text-[10.5px] text-[var(--color-text-dim)]">
                {t('profile.sections.qrcode.empty')}
              </span>
            )}
          </div>
        </div>

        {/* Invalid number (national format / too short) → warn, no QR. */}
        {digits.length > 0 && !validNumber && (
          <div className="rounded-xl border border-[var(--color-danger)]/40 bg-[var(--color-danger-bg)] px-3 py-2.5 text-[11.5px] leading-relaxed text-[var(--color-danger)]">
            {t('profile.sections.qrcode.invalidNumber')}
          </div>
        )}

        {/* QR + clickable link */}
        {validNumber && (
          <div className="rounded-2xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-4">
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
              <div className="shrink-0 rounded-xl bg-white p-2">
                <canvas ref={canvasRef} width={220} height={220} />
              </div>
              <div className="min-w-0 flex-1 space-y-3">
                <div>
                  <div className="mb-1 text-[11px] font-medium text-[var(--color-text-dim)]">
                    {t('profile.sections.qrcode.linkLabel')}
                  </div>
                  <a
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex max-w-full items-center gap-1.5 break-all rounded-lg border border-[var(--color-border-soft)] bg-[var(--color-surface-2)] px-2.5 py-1.5 text-[11.5px] text-[var(--color-link)] transition-colors hover:border-[var(--color-link)]"
                  >
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                    <span className="break-all">{link}</span>
                  </a>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => copyText('link', link)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[11.5px] font-medium text-[var(--color-text)] transition-colors hover:border-[var(--color-link)]"
                  >
                    {copied === 'link' ? (
                      <Check className="h-3.5 w-3.5 text-emerald-500" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                    {t('profile.sections.qrcode.copyLink')}
                  </button>
                  <button
                    type="button"
                    onClick={downloadQr}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[11.5px] font-medium text-[var(--color-text)] transition-colors hover:border-[var(--color-link)]"
                  >
                    <Download className="h-3.5 w-3.5" />
                    {t('profile.sections.qrcode.downloadQr')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
